import React, { useEffect, useMemo, useRef } from 'react';
import {
  AppBar,
  AppBarDataCustomItem,
  AppBarDataDropdown,
  Button,
  ExportGridRef,
  HeaderTabs,
} from '@ihsm/ui-shared';
import { TransparentButton } from 'shared/components/Buttons';
import { PageContent } from 'shared/components/PageContent';
import { ViewControls } from 'shared/components/ViewControls';
import { useHistory } from 'react-router-dom';
import * as ROUTES from 'routes';
import { useAppSelector } from 'state/store';
import { currentReportSelector } from 'state/store/selectors';
import { useGetReportsQuery } from 'services/newApi/reportsConfigApi';
import playIcon from 'shared/assets/icons/play.svg';
import { HeaderButtonWrapper } from 'shared/components/HeaderButtonWrapper';
import { formatDateForURL } from 'shared/utils';
import { FullViewLoading } from 'shared/components/FullViewLoading';
import { InnerPagePadding } from 'shared/components/InnerPagePadding';
import { SearchTreeReports } from '../components/SearchTreeReports';
import { useHeaderTabs, useRunReportForm } from '../hooks';
import { RunReportModalForm } from '../components/RunReportModalForm';
import { BENCH_OPTION, DATE_OPTION } from '../constants';
import { ReportSummaryGrid } from '../components/ReportSummaryGrid';
import { useReportsSummaryData } from '../hooks/useReportsSummaryData';
import './SummaryReport.scss';

export const SummaryReport: React.FC = () => {
  useGetReportsQuery();

  const {
    form,
    setForm,
    isLoading: isRunReportLoading,
    isModalVisible,
    runReport,
    setModalVisible,
  } = useRunReportForm();

  const {
    showLoading,
    isError,
    data,
    fundsList,
    selectedFund,
    setSelectedFund,
    columnList,
  } = useReportsSummaryData();

  const currentReport = currentReportSelector(useAppSelector((state) => state));
  const headerTabs = useHeaderTabs();
  const exporterRef = useRef<ExportGridRef>(null);
  const { push } = useHistory();

  const onExport = () => {
    exporterRef?.current?.exportGrid();
  };

  useEffect(() => {
    if (currentReport && (!currentReport?.jobId || !currentReport.jobTimestamp))
      push(ROUTES.REPORTS_CONFIG_REPORT);
  }, [currentReport, push]);

  const runReportButton: AppBarDataCustomItem = {
    type: 'customOption',
    component: (
      <HeaderButtonWrapper>
        <Button
          text="Run Report"
          onClick={() => setModalVisible(true)}
          iconSrc={playIcon}
          disabled={!currentReport}
        />
      </HeaderButtonWrapper>
    ),
  };

  const exportFileName = useMemo(
    () => `${currentReport?.name}_${formatDateForURL(new Date())}`,
    [currentReport]
  );

  const fundOption: AppBarDataDropdown = {
    type: 'dropdown',
    labelText: 'Fund',
    onChange: (value: string) => setSelectedFund(value),
    value: selectedFund,
    options: fundsList.map((fund) => ({ displayText: fund, value: fund })),
  };

  return (
    <div className="summary-report">
      <PageContent
        searchTree={<SearchTreeReports />}
        headerControls={
          <AppBar
            menuItems={[DATE_OPTION, fundOption, BENCH_OPTION, runReportButton]}
          >
            <HeaderTabs items={headerTabs} />
          </AppBar>
        }
        viewControls={
          <ViewControls>
            <TransparentButton
              text="Export"
              iconSrc="Download"
              onClick={onExport}
            />
          </ViewControls>
        }
      >
        <div className="summary-report-grid">
          <FullViewLoading
            forceLoadingScreen={showLoading}
            forceNoDataScreen={isError}
          >
            <InnerPagePadding>
              <ReportSummaryGrid
                data={data}
                columns={columnList}
                gridExportRef={exporterRef}
                exportFileName={exportFileName}
              />
            </InnerPagePadding>
          </FullViewLoading>
        </div>
      </PageContent>
      <RunReportModalForm
        form={form}
        setForm={setForm}
        onRun={runReport}
        onCancel={() => setModalVisible(false)}
        isLoading={isRunReportLoading}
        isVisible={isModalVisible}
        setVisible={setModalVisible}
      />
    </div>
  );
};
